import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  Plus, 
  Search,
  Filter,
  Smartphone, 
  Building, 
  CheckCircle, 
  XCircle, 
  Clock,
  Download,
  Eye,
  Calendar,
  Loader,
  AlertCircle
} from 'lucide-react'
import { DashboardStats, AppConfig, Build } from '../types'
import { mockApiCalls } from '../utils/mockData'

export function Dashboard() {
  const [builds, setBuilds] = useState<Build[]>([])
  const [filteredBuilds, setFilteredBuilds] = useState<Build[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [platformFilter, setPlatformFilter] = useState('all')

  useEffect(() => {
    const loadBuilds = async () => {
      try {
        const dashboardStats = await mockApiCalls.getDashboardStats()
        // Get builds from the stats and create proper Build objects
        const allBuilds: Build[] = dashboardStats.recentBuilds.map(build => ({
          ...build,
          progress: build.status === 'building' ? Math.floor(Math.random() * 90) + 10 : 100,
          startedAt: build.startedAt || new Date().toISOString(),
          buildLogs: []
        }))
        setBuilds(allBuilds)
        setFilteredBuilds(allBuilds)
      } catch (error) {
        console.error('Failed to load builds:', error)
      } finally {
        setLoading(false)
      }
    }

    loadBuilds()
  }, [])

  useEffect(() => {
    let filtered = builds

    if (searchTerm) {
      filtered = filtered.filter(build => 
        build.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        build.appId.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(build => build.status === statusFilter)
    }

    if (platformFilter !== 'all') {
      filtered = filtered.filter(build => build.platform === platformFilter)
    }

    setFilteredBuilds(filtered)
  }, [builds, searchTerm, statusFilter, platformFilter])

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    )
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-success-600" />
      case 'building':
        return <Loader className="w-5 h-5 text-brand-600 animate-spin" />
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-error-600" />
      case 'queued':
        return <Clock className="w-5 h-5 text-warning-600" />
      default:
        return <Building className="w-5 h-5 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-success-600 bg-success-50'
      case 'building':
        return 'text-brand-600 bg-brand-50'
      case 'failed':
        return 'text-error-600 bg-error-50'
      case 'queued':
        return 'text-warning-600 bg-warning-50'
      default:
        return 'text-gray-600 bg-gray-50'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatsCounts = () => {
    const total = builds.length
    const building = builds.filter(b => b.status === 'building').length
    const completed = builds.filter(b => b.status === 'success').length
    
    return { total, building, completed }
  }

  const stats = getStatsCounts()

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Build Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor and manage your app builds</p>
        </div>
        <Link 
          to="/configure" 
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create New App</span>
        </Link>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Builds</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="p-3 bg-brand-50 rounded-lg">
              <Building className="w-6 h-6 text-brand-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Building</p>
              <p className="text-3xl font-bold text-gray-900">{stats.building}</p>
            </div>
            <div className="p-3 bg-warning-50 rounded-lg">
              <Loader className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-3xl font-bold text-gray-900">{stats.completed}</p>
            </div>
            <div className="p-3 bg-success-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search builds..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="all">All Status</option>
            <option value="queued">Queued</option>
            <option value="building">Building</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
          
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="all">All Platforms</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>
        </div>
      </div>

      {/* Builds List */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Builds ({filteredBuilds.length})
          </h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {filteredBuilds.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No builds found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || statusFilter !== 'all' || platformFilter !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Create your first app to see builds here'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && platformFilter === 'all' && (
                <Link 
                  to="/configure" 
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create New App</span>
                </Link>
              )}
            </div>
          ) : (
            filteredBuilds.map((build) => (
              <div key={build.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(build.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(build.status)}`}>
                        {build.status}
                      </span>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900">
                        Build {build.id.slice(-8)}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="capitalize">{build.platform}</span>
                        <span>•</span>
                        <span className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(build.startedAt)}</span>
                        </span>
                        {build.status === 'building' && (
                          <>
                            <span>•</span>
                            <span>{build.progress}% complete</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {build.downloadUrl && (
                      <a 
                        href={build.downloadUrl}
                        className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                    <Link 
                      to={`/apps/${build.appId}`}
                      className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
                
                {build.status === 'building' && (
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-brand-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${build.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
} 